// Obsługi — wspólne słowa i rachunki Zwierząt i Sprzętów: gatunki (emoji) i rodzaje (ikony),
// gotowe podpowiedzi obsług według gatunku albo rodzaju, „za 5 dni · odrobaczanie", „co 3 mies.",
// wiek zwierzęcia, gwarancja. Używają ich strony /zwierzeta i /sprzety, Planer (wpis całodniowy)
// i „Teraz" asystenta. Teksty czytane leniwie (język zmienia się w trakcie).
import { t } from '@/i18n/index.js';
import { ref } from 'vue';
import { intlLocale } from '@/composables/useLanguage.js';
import { compressImage } from '@/composables/usePortfel.js';
import { askConfirm } from '@/composables/useConfirm.js';

export const SPECIES = ['dog', 'cat', 'rabbit', 'bird', 'fish', 'other'];
export const SPECIES_EMOJI = { dog: '🐕', cat: '🐈', rabbit: '🐇', bird: '🦜', fish: '🐟', other: '🐾' };

export const DEVICE_KINDS = ['car', 'appliance', 'electronics', 'home', 'bike', 'other'];
export const KIND_ICONS = {
    car: 'pi pi-car',
    appliance: 'pi pi-box',
    electronics: 'pi pi-desktop',
    home: 'pi pi-home',
    bike: 'pi pi-compass',
    other: 'pi pi-wrench',
};

// Gotowe obsługi do wyboru przy dodawaniu (nic z tego nie siedzi w bazie): klucz tekstu, co ile miesięcy
// i — przy aucie — co ile km (co nastąpi pierwsze).
const PET_CARES = [['vaccine', 12], ['deworming', 3], ['ticks', 1], ['vet', 12], ['grooming', 2], ['claws', 1]];
const SUGGESTIONS = {
    pet: { dog: PET_CARES, cat: PET_CARES },
    device: {
        car: [['inspection', 12], ['insurance', 12], ['oil', 12, 15000], ['timing', 60, 90000], ['tyres', 6]],
        appliance: [['descaling', 3], ['filter', 6], ['service', 12]],
    },
};
const FALLBACK = [['service', 12]];

// Podpowiedzi dla zwierzęcia (type 'pet', gatunek) albo sprzętu ('device', rodzaj): { name, every, everyKm }.
export const careSuggestions = (type, kind) => (SUGGESTIONS[type]?.[kind] ?? FALLBACK)
    .map(([key, every, everyKm = null]) => ({ key, name: t(`cares.suggestions.${key}`), every, everyKm }));

// „123 400".
export const kmNumber = (km) => Number(km).toLocaleString(intlLocale(), { maximumFractionDigits: 0 });

// „co 3 mies.", „co 15 000 km", „co 12 mies. lub 15 000 km" albo „jednorazowa".
export const careEveryLabel = (months, km = null) => {
    if (months && km) return t('cares.everyOr', { months: t('cares.every', { n: months }), km: kmNumber(km) });
    if (km) return t('cares.everyKm', { km: kmNumber(km) });
    return months ? t('cares.every', { n: months }) : t('cares.once');
};

// „zostało ~4 000 km" albo „500 km po progu".
export const kmLeftLabel = (left) => (left >= 0
    ? t('cares.km.left', { km: kmNumber(left) })
    : t('cares.km.over', { km: kmNumber(-left) }));

// Dni od dziś do dnia (YYYY-MM-DD), w strefie urządzenia; ujemne = minął.
export const daysTo = (iso, now = new Date()) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    const then = new Date(y, m - 1, d);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((then - today) / 86400000);
};

// „dziś", „jutro", „za 5 dni", „3 dni po terminie".
export const careDueLabel = (iso) => {
    const n = daysTo(iso);
    if (n == null) return null;
    if (n < 0) return t('cares.when.late', { n: -n }, -n);
    if (n === 0) return t('cares.when.today');
    if (n === 1) return t('cares.when.tomorrow');
    return t('cares.when.inDays', { n }, n);
};

// Nazwa obsługi w zdaniu: „odrobaczanie", ale „OC" zostaje.
export const careWord = (name) => {
    const s = String(name ?? '');
    return s.length > 1 && s[1] === s[1].toLowerCase() ? s[0].toLowerCase() + s.slice(1) : s;
};

// Plakietka najbliższej obsługi na liście: „za 5 dni · odrobaczanie".
export const careBadge = (next) => (next ? t('cares.badge', { when: careDueLabel(next.due_on), what: careWord(next.name) }) : null);

// Tytuł wpisu w Planerze: „💉 Burek: szczepienie" albo „🔧 Pralka: odkamienianie".
export const carePlannerTitle = (care) => t('cares.planner', {
    icon: care.subject?.type === 'pet' ? '💉' : '🔧',
    subject: care.subject?.name ?? '',
    name: careWord(care.name),
});

// Link do zwierzęcia albo sprzętu.
export const subjectLink = (subject) => (subject?.type === 'pet'
    ? (subject.uuid ? `/zwierzeta?z=${subject.uuid}` : '/zwierzeta')
    : (subject?.uuid ? `/sprzety?s=${subject.uuid}` : '/sprzety'));

// Wiek zwierzęcia z daty urodzenia: „3 lata", „5 mies.", „2 tyg."; null bez daty.
export const petAgeLabel = (birthDate, now = new Date()) => {
    if (!birthDate) return null;
    const [y, m, d] = String(birthDate).slice(0, 10).split('-').map(Number);
    let months = (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
    if (now.getDate() < d) months--;
    if (months >= 12) {
        const years = Math.floor(months / 12);
        return t('pets.age.years', { n: years }, years);
    }
    if (months >= 1) return t('pets.age.months', { n: months });
    const days = Math.max(0, -daysTo(birthDate, now));
    return t('pets.age.weeks', { n: Math.floor(days / 7) });
};

// Gwarancja: { text, tone } — „kończy się za 12 dni" (pomarańczowo w ciągu 30 dni), „do 1 marca 2027", „po gwarancji".
export const warrantyState = (until) => {
    if (!until) return null;
    const n = daysTo(until);
    if (n < 0) return { text: t('devices.warranty.ended'), tone: 'ended' };
    if (n === 0) return { text: t('devices.warranty.endsToday'), tone: 'soon' };
    if (n <= 30) return { text: t('devices.warranty.endsIn', { n }, n), tone: 'soon' };
    return { text: t('devices.warranty.until', { date: careDayLabel(until) }), tone: 'ok' };
};

// „14 mar 2026".
export const careDayLabel = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// Zapisy obsług ze strony zwierzęcia albo sprzętu (wspólne dla obu): po każdej zmianie strona
// odświeża otwarte zwierzę / sprzęt z API (refresh), bo termin i koszty liczy serwer.
// Usunięcie obsługi najpierw ją chowa, a do API idzie, gdy minie czas na „Cofnij".
export function useCareActions({ api, withZone, notify, refresh, firstSteps, subjectPath }) {
    const run = async (subject, call, failed = 'common.errors.saveRetry', step = false) => {
        try {
            await call();
            if (step) firstSteps.changed();
            await refresh(subject.uuid);
        } catch {
            notify(t(failed));
        }
    };

    return {
        add: (subject, body) => run(subject, () => api.post(withZone(`${subjectPath(subject)}/cares`), body), undefined, true),
        update: (subject, care, body) => run(subject, () => api.patch(withZone(`/cares/${care.uuid}`), body)),
        done: (subject, care, body) => run(subject, () => api.post(withZone(`/cares/${care.uuid}/logs`), body), undefined, true),
        updateLog: (subject, log, body) => run(subject, () => api.patch(withZone(`/care-logs/${log.uuid}`), body)),
        // withExpense: usuń też wydatek w Portfelu, który powstał z kosztu
        removeLog: (subject, log, withExpense = false) => run(subject, () => api.delete(`/care-logs/${log.uuid}${withExpense ? '?with_expense=1' : ''}`), 'common.errors.deleteFailed'),
        remove: async (subject, care) => {
            const logs = (care.logs ?? []).length;
            const ok = await askConfirm({
                title: t('common.confirmAsk.care'),
                subject: care.name,
                message: logs ? t('common.confirmAsk.careLogs', { n: logs }, logs) : t('common.confirm.undoable'),
            });
            if (!ok) return;
            const index = subject.cares.indexOf(care);
            subject.cares.splice(index, 1);
            let undone = false;
            notify(t('cares.removed', { name: care.name }), () => {
                undone = true;
                subject.cares.splice(index, 0, care);
            }, () => {
                if (!undone) run(subject, () => api.delete(`/cares/${care.uuid}`), 'common.errors.deleteFailed');
            });
        },
    };
}

// Czy Portfel jest uruchomiony — wtedy „Zrobione" z kosztem domyślnie dodaje wydatek. Pytamy raz na sesję.
const walletStarted = ref(false);
let walletAsked = null;
export const useWalletStarted = (api) => {
    walletAsked ??= api.get('/wallet').then((r) => { walletStarted.value = !!r?.started; }).catch(() => { walletAsked = null; });
    return walletStarted;
};

// ——— Dokumenty ———

// Plik dokumentu jako adres w przeglądarce — dostępny tylko z tokenem, więc pobieramy go jako blob;
// adresy trzymamy, żeby miniatury nie pobierały się co chwilę.
const documentUrls = new Map();
export const documentUrl = async (api, uuid) => {
    if (!documentUrls.has(uuid)) {
        const blob = await api.get(`/documents/${uuid}`, { responseType: 'blob' });
        documentUrls.set(uuid, URL.createObjectURL(blob));
    }
    return documentUrls.get(uuid);
};

// Dokument do zwierzęcia (/pets/…), sprzętu (/devices/…) albo wykonania (/care-logs/…): zdjęcie zmniejszone
// jak paragon w Portfelu, PDF bez zmian.
export const uploadDocument = async (api, parentPath, file, title = null) => {
    const form = new FormData();
    const small = await compressImage(file);
    form.append('file', small, small.name);
    if (title) form.append('title', title);
    return api.post(`${parentPath}/documents`, form);
};

// Kolejność listy: najpierw najbliższa obsługa (po terminie na górze), potem alfabetycznie.
export const compareByCare = (a, b) => {
    const da = a.next_care ? daysTo(a.next_care.due_on) : Infinity;
    const db = b.next_care ? daysTo(b.next_care.due_on) : Infinity;
    if (da !== db) return da - db;
    return a.name.localeCompare(b.name, intlLocale());
};
